import updateCabinetVisualizer from './updateCabinetVisualizer';
import { createDesignWithProject, saveCabinetDesign } from '../../../api/design';
import { setQueryParam } from '../../../utils/queryParams';
import { useDrawerStore } from '../../ui';

/**
 * Converts a temp design (temp-*) to a real persisted design on the backend.
 * This is used when "Add Wall" is clicked before any cabinets have been added.
 *
 * Flow:
 * 1. Calls createDesignWithProject API to allocate a real designId + projectId
 * 2. Builds a design object from the existing temp design data
 * 3. Saves the design to the backend via saveCabinetDesign
 * 4. Updates the store: replaces temp ID with real ID in designs, designIds, styleDesignIds
 * 5. Updates URL query params
 *
 * @param {Function} set - Zustand set function
 * @param {Function} get - Zustand get function
 * @param {string} host - API host URL
 * @param {string} tempDesignId - The temp design ID to convert (e.g. "temp-248904")
 * @param {Function} navigate - React Router navigate function for URL updates
 * @param {Object} [options] - Optional configuration
 * @param {boolean} [options.skipUrlUpdate=false] - When true, skip updating the URL designid param.
 *   Use this when createAndUpdateDesign will immediately follow and set the URL itself,
 *   preventing the hydration useEffect from firing on an intermediate designId.
 * @returns {string|null} The new real designId, or null on failure
 */
export default async function convertTempDesign(set, get, host, tempDesignId, navigate, options = {}) {
  try {
    const state = get().cabinetVisualizer;
    const styleChoiceId = state.styleChoiceId;
    const currentProjectId = state.currentProjectId;
    const projects = state.projects || {};
    const existingTempDesign = state.designs[tempDesignId];

    if (!existingTempDesign) {
      // eslint-disable-next-line no-console
      console.warn(`convertTempDesign: Design with ID "${tempDesignId}" not found in state.`);
      return null;
    }

    // 1. Create a real design ID and project via the API
    const result = await createDesignWithProject(host, 15, 'Cabinet', currentProjectId);

    if (!result) {
      throw new Error('Failed to create design during temp conversion');
    }

    const { designId: newDesignId, projectId: resultProjectId } = result;

    const selectedCollection = useDrawerStore.getState().selectedCollection;

    // 2. Build the real design object, preserving existing temp design data
    const newDesignObject = {
      ...existingTempDesign,
      ProductTypeId: 15,
      DesignStyleChoiceid: styleChoiceId,
      CollectionId: selectedCollection?.itemId,
      projectId: resultProjectId,
      Id: newDesignId,
      isTemp: false,
    };
    // Remove any temp-specific internal fields
    delete newDesignObject.isTemp;

    // 3. Persist to backend
    await saveCabinetDesign(host, newDesignObject);

    // 4. Update projects metadata
    let updatedProjects = { ...projects };

    if (!currentProjectId && resultProjectId) {
      updatedProjects[resultProjectId] = {
        id: resultProjectId,
        styleChoiceId: parseInt(styleChoiceId, 10),
        name: `${existingTempDesign.DesignName || 'Wall A'} Project`,
        created: new Date().toISOString(),
        lastModified: new Date().toISOString(),
        designIds: [newDesignId]
      };
    } else if (currentProjectId && resultProjectId) {
      const existingProject = updatedProjects[resultProjectId] || {};
      const existingDesignIds = existingProject.designIds || [];
      updatedProjects[resultProjectId] = {
        ...existingProject,
        designIds: [...existingDesignIds, newDesignId],
        lastModified: new Date().toISOString(),
      };
    }

    // 5. Replace temp ID with real ID in all store arrays
    const newDesignIds = state.designIds.map((id) => (id === tempDesignId ? newDesignId : id));
    const newStyleDesignIds = state.styleDesignIds.map((id) => (id === tempDesignId ? newDesignId : id));

    // Build updated designs: add real, remove temp
    const newDesigns = { ...state.designs, [newDesignId]: newDesignObject };
    delete newDesigns[tempDesignId];

    // Determine selected design: if the temp was selected, switch to real
    const newSelectedDesignId = state.selectedDesignId === tempDesignId
      ? newDesignId
      : state.selectedDesignId;

    // 6. Update URL BEFORE store to ensure that when the store update triggers a
    // re-render, the hydration useEffect reads the correct designId from the URL.
    // When skipUrlUpdate is true (i.e. createAndUpdateDesign follows), we skip this
    // to prevent the hydration hook from firing on this intermediate designId.
    if (!options.skipUrlUpdate) {
      setQueryParam('designid', newDesignId);
    }

    // 7. Update store
    updateCabinetVisualizer(set, get, {
      selectedDesignId: newSelectedDesignId,
      designIds: newDesignIds,
      styleDesignIds: newStyleDesignIds,
      projects: updatedProjects,
      currentProjectId: resultProjectId || currentProjectId,
      designs: newDesigns,
    });

    return newDesignId;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.log(`Convert Temp Design Error: ${err}`);
    return null;
  }
}
